import { useEffect, useMemo, useState } from "react";
import { useHydrateAtoms } from "jotai/utils";
import { Footer, type FooterProps } from "../../components/Footer";
import { Header, type HeaderProps } from "../../components/Header";
import { PreFooter } from "../../components/PreFooter";
import type { PreFooterProps } from "../../components/PreFooter/types";
import { ProgressSteps } from "../../components/ProgressSteps";
import { useExperiments } from "../../hooks/experiments";
import { useSetTokens } from "../../hooks/tokens";
import { useLocale } from "../../hooks/useLocale";
import { environmentAtom, tokensAtom } from "../../store";
import type { Locale } from "../../types";
import type {
  PageProps,
  ProgressStepsContentProps,
  WithMetadata,
} from "./types";
import {
  RewardBanner,
  type RewardBannerProps,
} from "../../components/RewardBanner";
import {
  useRewardBanner,
  useSetRewardBanner,
} from "../../hooks/useRewardBanner";
import { IndicativePricingModalIntervention } from "../../components/IndicativePricingModalIntervention";
import "../Main.css";

export const Page = (props: PageProps) => {
  const {
    configuration,
    layout: { /* metadata, */ components /* variations */ },
    children,
    experiments,
  } = props;
  const {
    authenticityToken,
    journeyToken,
    site,
    journeyId,
    vertical,
    affclie,
  } = configuration;

  // Hydrate server-provided state
  useExperiments(experiments);
  useLocale(configuration.locale as Locale);
  useHydrateAtoms([
    [tokensAtom, { authenticityToken, journeyToken }],
    [environmentAtom, { site, journeyId, vertical, affclie }],
  ]);

  const [pageReady, setPageReady] = useState(false);

  // Keep tokens in sync for post-render changes (e.g. token refresh)
  const setTokens = useSetTokens();

  useEffect(() => {
    setTokens({ authenticityToken, journeyToken });
    setPageReady(true);
  }, [authenticityToken, journeyToken, setTokens]);

  // Single pass: build Map then O(1) lookups (avoids 6 separate .find() calls)
  const componentProps = useMemo(() => {
    const componentMap = new Map<string, unknown>();
    for (const component of components ?? []) {
      if (!component?.metadata?.name) continue;
      const props = {
        ...((component.props || {}) as Record<string, unknown>),
        componentName: component.metadata.name,
        variant: component.metadata.variant,
        ...(component.actions && { actions: component.actions }),
      };
      componentMap.set(component.metadata.name, props);
    }
    return {
      header: componentMap.get("header") as WithMetadata<HeaderProps>,
      progressSteps: componentMap.get(
        "progress-steps",
      ) as WithMetadata<ProgressStepsContentProps>,
      preFooter: componentMap.get("pre-footer") as WithMetadata<PreFooterProps>,
      footer: componentMap.get("footer") as WithMetadata<FooterProps>,
      rewardBanner: componentMap.get(
        "reward-banner",
      ) as WithMetadata<RewardBannerProps>,
      indicativePricingModal: componentMap.get(
        "indicative-pricing-modal-intervention",
      ) as WithMetadata<Record<string, unknown>> | undefined,
    };
  }, [components]);

  const {
    header: headerComponentProps,
    progressSteps: progressComponentProps,
    preFooter: preFooterComponentProps,
    footer: footerComponentProps,
    rewardBanner: initialRewardBannerProps,
    indicativePricingModal: indicativePricingModalInterventionProps,
  } = componentProps;

  useSetRewardBanner(initialRewardBannerProps);
  const [rewardBannerComponentProps] = useRewardBanner();

  return (
    <div
      className="mobius-page__container"
      data-page-loaded={pageReady}
      data-testid="base-page"
    >
      {rewardBannerComponentProps && (
        <RewardBanner {...rewardBannerComponentProps} />
      )}
      <Header {...headerComponentProps} />
      {indicativePricingModalInterventionProps && (
        <IndicativePricingModalIntervention
          {...indicativePricingModalInterventionProps}
        />
      )}
      {progressComponentProps && <ProgressSteps {...progressComponentProps} />}
      {children}
      {preFooterComponentProps && <PreFooter {...preFooterComponentProps} />}
      <Footer {...footerComponentProps} />
    </div>
  );
};
